import { goRequest, formGoClient } from '../utils/httpClientTs';
import {
  IRSFormListResponse,
  IRSListParams,
  IRSFormDetail,
  IRSSection,
  IRSQuestion,
  IRSOtherLineItem,
  UpdateIRSFormRequest,
  UpdateAnswerRequest,
  CreateOtherLineItemRequest,
  UpdateOtherLineItemRequest,
  IRSApprovalRequest,
  IRSRejectionRequest,
  IRSGenericResponse,
  UpdateAnswerResponse,
  IRSDocumentResponse,
  IRSSummaryResponse,
  IRSSectionMetadata,
} from '../types/irsForms';

export const getIRSFormsList = async (
  params: IRSListParams
): Promise<IRSFormListResponse> => {
  let url = `/api/annual-reports/irs?page_size=${params.page_size}&page=${params.page}&year=${params.year}`;

  if (params.search) {
    url += `&search=${encodeURIComponent(params.search)}`;
  }
  if (params.order_by) {
    url += `&order_by=${params.order_by}`;
  }
  if (params.order) {
    url += `&order=${params.order}`;
  }
  if (params.assigned_to_me) {
    url += `&assigned_to_me=${params.assigned_to_me}`;
  }
  if (params.status_id) {
    url += `&status_id=${encodeURIComponent(params.status_id)}`;
  }
  if (params.irs_type_id) {
    url += `&irs_type_id=${encodeURIComponent(params.irs_type_id)}`;
  }
  if (params.form_type) {
    url += `&form_type=${params.form_type}`;
  }
  if (params.spd_chapter_id) {
    url += `&spd_chapter_id=${encodeURIComponent(params.spd_chapter_id)}`;
  }

  return goRequest<IRSFormListResponse>({
    url: url,
    method: 'GET',
  });
};

// Local Chapter endpoints
export const getLocalChapterIRSFormDetail = async (
  id: string
): Promise<IRSFormDetail> => {
  return goRequest<IRSFormDetail>({
    url: `/api/annual-reports/irs/local-chapter/${id}`,
    method: 'GET',
  });
};

// SPD endpoints
export const getSPDIRSFormDetail = async (
  id: string
): Promise<IRSFormDetail> => {
  return goRequest<IRSFormDetail>({
    url: `/api/annual-reports/irs/spd/${id}`,
    method: 'GET',
  });
};

// Reciprocity endpoints
export const getReciprocityIRSFormDetail = async (
  id: string
): Promise<IRSFormDetail> => {
  return goRequest<IRSFormDetail>({
    url: `/api/annual-reports/irs/reciprocity/${id}`,
    method: 'GET',
  });
};

// Generic detail endpoint (for when form type is unknown)
export const getIRSFormDetail = async (id: string): Promise<IRSFormDetail> => {
  return goRequest<IRSFormDetail>({
    url: `/api/annual-reports/irs/${id}`,
    method: 'GET',
  });
};

export const updateLocalChapterIRSForm = async (
  id: string,
  data: UpdateIRSFormRequest
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/local-chapter/${id}`,
    method: 'PATCH',
    data: data,
  });
};

export const updateSPDIRSForm = async (
  id: string,
  data: UpdateIRSFormRequest
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/spd/${id}`,
    method: 'PATCH',
    data: data,
  });
};

export const updateReciprocityIRSForm = async (
  id: string,
  data: UpdateIRSFormRequest
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/reciprocity/${id}`,
    method: 'PATCH',
    data: data,
  });
};

export const getIRSFormSections = async (id: string): Promise<IRSSection[]> => {
  return goRequest<IRSSection[]>({
    url: `/api/annual-reports/irs/${id}/sections`,
    method: 'GET',
  });
};

export const getIRSFormQuestions = async (
  id: string,
  sectionId?: string
): Promise<IRSQuestion[]> => {
  let url = `/api/annual-reports/irs/${id}/questions`;
  if (sectionId) {
    url += `?section_id=${encodeURIComponent(sectionId)}`;
  }
  return goRequest<IRSQuestion[]>({
    url: url,
    method: 'GET',
  });
};

export const getIRSFormSummary = async (
  id: string
): Promise<IRSSummaryResponse> => {
  return goRequest<IRSSummaryResponse>({
    url: `/api/annual-reports/irs/${id}/summary`,
    method: 'GET',
  });
};

export const getIRSSectionMetadata = async (
  formId: string,
  sectionId: string
): Promise<IRSSectionMetadata> => {
  return goRequest<IRSSectionMetadata>({
    url: `/api/annual-reports/irs/${formId}/sections/${sectionId}/metadata`,
    method: 'GET',
  });
};

export const updateIRSFormAnswer = async (
  id: string,
  questionId: string,
  data: UpdateAnswerRequest
): Promise<UpdateAnswerResponse> => {
  return goRequest<UpdateAnswerResponse>({
    url: `/api/annual-reports/irs/${id}/questions/${questionId}/answer`,
    method: 'PUT',
    data: data,
  });
};

// Update answer with file upload (for image type questions)
export const updateIRSFormAnswerWithFile = async (
  id: string,
  questionId: string,
  formData: FormData
): Promise<UpdateAnswerResponse> => {
  return formGoClient.put(
    `/api/annual-reports/irs/${id}/questions/${questionId}/answer`,
    formData
  );
};

export const getIRSFormOtherItems = async (
  id: string,
  questionId: string
): Promise<IRSOtherLineItem[]> => {
  return goRequest<IRSOtherLineItem[]>({
    url: `/api/annual-reports/irs/${id}/questions/${questionId}/other-items`,
    method: 'GET',
  });
};

export const createIRSFormOtherItem = async (
  id: string,
  questionId: string,
  data: CreateOtherLineItemRequest
): Promise<IRSOtherLineItem> => {
  return goRequest<IRSOtherLineItem>({
    url: `/api/annual-reports/irs/${id}/questions/${questionId}/other-items`,
    method: 'POST',
    data: data,
  });
};

export const updateIRSFormOtherItem = async (
  id: string,
  questionId: string,
  itemId: string,
  data: UpdateOtherLineItemRequest
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/${id}/questions/${questionId}/other-items/${itemId}`,
    method: 'PUT',
    data: data,
  });
};

export const deleteIRSFormOtherItem = async (
  id: string,
  questionId: string,
  itemId: string
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/${id}/questions/${questionId}/other-items/${itemId}`,
    method: 'DELETE',
  });
};

export const approveIRSForm = async (
  id: string,
  data: IRSApprovalRequest
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/${id}/approval`,
    method: 'POST',
    data: data,
  });
};

export const rejectIRSForm = async (
  id: string,
  data: IRSRejectionRequest
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/${id}/rejection`,
    method: 'POST',
    data: data,
  });
};

// Document endpoints

/**
 * @deprecated Use question.document from GET questions response instead
 * Document metadata is now included in the questions response
 */
export const getIRSFormDocuments = async (
  formId: string,
  questionId?: string
): Promise<IRSDocumentResponse[]> => {
  let url = `/api/annual-reports/irs/${formId}/documents`;
  if (questionId) {
    url += `?question_id=${encodeURIComponent(questionId)}`;
  }
  return goRequest<IRSDocumentResponse[]>({
    url: url,
    method: 'GET',
  });
};

/**
 * @deprecated Use updateIRSFormAnswerWithFile instead
 * Documents should be uploaded via PUT /api/annual-reports/irs/:id/questions/:questionId/answer
 */
export const uploadIRSFormDocument = async (
  formId: string,
  questionId: string,
  file: File,
  documentType: 'receipt' | 'supporting_document' | 'other'
): Promise<IRSDocumentResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('question_id', questionId);
  formData.append('document_type', documentType);

  const response = await formGoClient.post(
    `/api/annual-reports/irs/${formId}/documents`,
    formData
  );
  return response.data;
};

export const deleteIRSFormDocument = async (
  formId: string,
  documentId: string
): Promise<IRSGenericResponse> => {
  return goRequest<IRSGenericResponse>({
    url: `/api/annual-reports/irs/${formId}/documents/${documentId}`,
    method: 'DELETE',
  });
};

// Get signed URL for document viewing/downloading
export const getIRSFormDocumentUrl = async (
  formId: string,
  documentId: string
): Promise<{ url: string }> => {
  return goRequest<{ url: string }>({
    url: `/api/annual-reports/irs/${formId}/documents/${documentId}/url`,
    method: 'GET',
  });
};
